<template>
    <base-page>
        <CustomTable
            class=""
            :module-name="moduleName"
            :module-name-singular="moduleNameSingular"
            :response="response"
            :isLoading="isLoading"
            :tableHeaders="tableHeaders"
            :filters="clientFilters"
            :bulkOperations="clientBulkOperations"
            :menuOptions="menuOptions"
            :defaultOrdering="defaultOrdering"
            :shouldReload="shouldReload"
            :shouldClearSelected="shouldClearSelected"
            :bulkOperationResponse="bulkOperationResponse"
            :reorder-bulk-operations="reorderBulkOperations"
            :reorder-filters="reorderFilters"
            createModalSize="w-3/4 h-5/6"
            @refetch-results="loadClients"
            @clear-filter="clearFilter"
            @change-filters="setFilters"
            @set-filter-options="setFilterOptions"
            @toggle-pinned-filter="togglePinnedFilter"
        >
            <template #summaries>
                <div class="flex text-xs whitespace-nowrap h-full items-center mr-2">
                    Completed Workflow Steps: {{ totalWorkflowStepsRatio }}
                </div>
            </template>
            <template #create-form="{ events: { closeCreateForm, refetchResults }, fetchFilter }">
                <ClientForm
                    add-or-edit="add"
                    @refetch-clients="refetchResults(fetchFilter)"
                    @close="closeCreateForm"
                />
            </template>
            <template #table-row="{ events: { addSelectedResult, removeSelectedResult, refetchResults, openRowDetails }, selectAll, expandAll, fetchFilter, userPermissions, results, clearedSelectedResults }">
                <ClientRowNew
                    v-for="client in results"
                    :key="client.id"
                    :selectAll="selectAll"
                    :expandAll="expandAll"
                    :userPermissions="userPermissions"
                    :clearedSelectedResults="clearedSelectedResults"
                    :client="client"
                    :workflow-steps="workflowSteps"
                    @refetch-results="refetchResults(fetchFilter)"
                    @select-row="addSelectedResult"
                    @deselect-row="removeSelectedResult"
                    @open-row-details="openRowDetails"
                    @perform-row-action="performRowAction"
                />
                <tr class="bg-gray-200 h-60">
                    <td :colspan="columnSpan" :class="{ 'border-t border-gray-300' : results && results.length > 0}"></td>
                </tr>
            </template>
            <!-- <template #details-page="{ events: { closeDetails, refetchResults }, resultRow, userPermissions, fetchFilter }">
                <CompanyDetails
                    :company="resultRow"
                    :user-permissions="userPermissions"
                    :client-options="clientOptions"
                    @close-details="closeDetails"
                    @refetch-results="refetchResults(fetchFilter)"
                />
            </template> -->
            <template #table-footer="{ events: { getNext, goToFirst, getPrev, goToLast, jumpToPage, setPageSizeFilter }, viewingFromNbr, viewingToNbr, count, selectedResults, totalPages, page, results, next, prev }">
                <CompanyFooter
                    :is-loading="isLoading"
                    :viewingFromNbr="viewingFromNbr"
                    :viewingToNbr="viewingToNbr"
                    :count="count"
                    :selectedResults="selectedResults"
                    :numPages="totalPages"
                    :results="results"
                    :page="page"
                    :column-span="columnSpan"
                    :has-next="next"
                    :has-prev="prev"
                    @get-next="getNext"
                    @go-to-first="goToFirst"
                    @get-prev="getPrev"
                    @go-to-last="goToLast"
                    @jump-to-page="jumpToPage"
                    @set-page-size-filter="setPageSizeFilter"
                />
            </template>
        </CustomTable>
        <base-modal :is-form-visible="visibleEditForm" @close="closeEditForm">
        <template v-slot:header> {{ displayedClient.client_name }} (ID: {{ displayedClient.id }}) </template>
            <template v-slot:body>
                <ClientModal
                @submit-edit-form="submitEditForm"
                @close-edit-form="closeEditForm"
                :client="displayedClient"
                :client-task-types="clientTaskTypes"
                :default-tab="defaultTab"
                :workflow-steps="workflowSteps"
                @refetch-clients="reloadClients"
                addOrEdit="edit"
                />
            </template>
        </base-modal>
        <base-modal :is-form-visible="visibleEngagementsTable" @close="closeEngagementsTable">
        <template v-slot:header> {{ displayedClient.client_name }} Aerocle ID: {{ displayedClient.id }} </template>
            <template v-slot:body>
            <div v-if="displayedClient.engagements.length > 0" class="h-full overflow-y-auto">
            <base-table>
                <template #table-header>
                    <base-table-header class="text-center" :key="header.value" :header="header" v-for="header in engagementTableHeadersList"> 
                    <div class="flex  items-center justify-between space-x-1">
                        <div>
                        {{ header.value }}
                        </div>
                    </div>
                    </base-table-header>
                    </template>

                    <template #table-body>
                    <EngagementRow
                        :key="engagement.id"
                        :engagement="engagement"
                        :clients="response.data.results"
                        :client="displayedClient"
                        @refetch-engagements="reloadClients"
                        v-for="engagement in displayedClient.engagements"
                                />
                    </template>
                </base-table>
            </div>
            </template>
        </base-modal>
        <base-modal 
        :is-form-visible="visibleInvoicesTable" @close="closeInvoicesTable"
            >
              <template v-slot:header>
                All Invoices for {{ displayedClient.client_name }}:
              </template>
              <template v-slot:body>
                <div class="max-h-[95vh] overflow-auto">
                    <SimpleInvoiceTable :invoices="displayedInvoices" />
                </div>
              </template>
        </base-modal>
        <InfoModal
            :is-modal-visible="visibleContactList"
            @close="closeContactList"
        >
            <template #header>
                All Contacts
            </template>
            <template #body>
                <AllClientContactsList :client="displayedClient" @refetch-clients="reloadClients" @close="closeContactList" />
            </template>
        </InfoModal>
        <InfoModal
            :is-modal-visible="visibleNewContactForm"
            @close="closeNewContactForm"
        >
            <template #header>
                Add Contact
            </template>
            <template #body>
                <ClientContactFormVertical
                    :client="displayedClient"
                    @refetch-clients="reloadClients"
                    @close-form="closeNewContactForm"
                />
            </template>
        </InfoModal>
    </base-page>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import CustomTable from '../base-components/CustomTable.vue';
import ClientRowNew from './ClientRowNew.vue';
import CompanyFooter from '../company/CompanyFooter.vue';
import { getClients, createClient, editClient } from "@/services/Client.js";
import { getClientTaskTypes } from '@/services/ClientTaskTypes';
import { formatDateTwoDigitYear } from '@/composables/formatDate';
import { getWorkflowSteps } from "@/services/Workflow.js";
import ClientModal from './ClientModal.vue';
import EngagementRow from '../engagement-components/EngagementRow.vue';
import SimpleInvoiceTable from '../invoice_components/SimpleInvoiceTable.vue';
import ClientForm from './ClientForm.vue';
import AllClientContactsList from './AllClientContactsList.vue';
import { getUsers } from '@/services/User';
import InfoModal from '../InfoModal.vue';
import ClientContactFormVertical from './client-contact-components/ClientContactFormVertical.vue';

const store = useStore()
const $toast = useToast()

const isLoading = ref(false)
const moduleName = ref('Clients')
const moduleNameSingular = ref('Client');
const response = ref(null)
const defaultOrdering = ref([])
const shouldReload = ref(false)
const shouldClearSelected = ref(false)
const bulkOperationResponse = ref()
const reorderBulkOperations = ref(false)
const reorderFilters = ref(false)
const visibleEditForm = ref(false)
const visibleEngagementsTable = ref(false)
const visibleInvoicesTable = ref(false)
const visibleNewContactForm = ref(false)
const visibleContactList = ref(false)
const actionButtonId = ref(null)
const defaultTab = ref(null)
const tableHeaders = ref([
    {
        value: '',
        label: '',
        class: 'p-0 h-[33px] w-[2%] bg-gray-150 border-b-2'
    },
    {
        value: 'client',
        label: 'Client',
        class: 'p-0 w-[15%] bg-gray-150 border-b-2',
        orderFieldName: 'client_name'
    },
    {
        value: 'engagements',
        label: 'Engagements',
        class: 'p-0 w-[15%] bg-gray-150 border-b-2',
        orderFieldName: 'engagements__start_date'
    },
    {
        value: 'engagement_invoice_data',
        label: 'Engagement Invoice Data',
        class: 'p-0 w-[20%] bg-gray-150 border-b-2',
        orderFieldName: 'next_invoice_day'
    },
    {
        value: 'client_payment_data',
        label: 'Payment Data',
        class: 'p-0 w-[15%] bg-gray-150 border-b-2',
    },
    {
        value: 'missing_workflow_steps',
        label: 'Missing Workflow Steps',
        class: 'p-0 w-[20%] bg-gray-150 border-b-2',
    },
    {
        value: 'client_posts',
        label: 'Client Posts',
        class: 'p-0 w-[20%] bg-gray-150 border-b-2',
    },
    {
        value: 'contacts',
        label: 'Contacts',
        class: 'p-0 w-[20%] bg-gray-150 border-b-2',
    },
])

const menuOptions = reactive({
    controlPanel: {
        width: 'w-[330px]',
        panes: ['filters'],
    },
    loading: {
        rowClass: 'h-48 bg-inherit border-r border-b border-gray-300'
    },
    fetchResultsOnMount: true,
    defaultPageSize: 50,
    quickSearch: false,
    selectAll: false,
    selectAllHeader: false,
    expandAllHeader: false,
    paginationArrows: false,
    totalResults: false,
    pageSize: false,
    refreshButton: false,
    createForm: true,
    addFormRows: false,
    bulkOperations: false,
    summaries: true
})

const engagementTableHeadersList = ref([
{value: 'ID'},
{value: 'Client', orderFieldName: 'client__client_name'},
{value: 'Start Date', orderFieldName: 'start_date'},
{value: 'End Date', orderFieldName: 'end_date'},
{value: 'Retainer Amount'},
{value: 'Payment Terms'},
{value: 'Billing Timeframe'},
{value: 'Invoice Due Delta'},
{value: 'Next Calc. Invoice Day'},
{value: 'Days Until Next Calc. Invoice Day'},
{value: 'Target Due Date'},
{value: 'Last Invoice Nbr: Date (Due)'},
// {value: 'Missing Invoice'},
{value: 'Typical Payday'},
{value: 'Accounting Posts'},
])

const clientFilters = reactive({
    status: {
        id: 22,
        name: "status",
        label: "Status",
        value: [],
        defaultValue: [],
        placeholder: '',
        options: [
            {value: null, label: "------------"},
            {value: "active", label: "active"},
            {value: "prospect", label: "prospect"},
            {value: "finders fee", label: "finders fee"},
            {value: "tail", label: "tail"},
            {value: "inactive", label: "inactive"}
        ],
        widget: 'list-box',
        multiSelect: false,
        awaitingOptions: false,
        visible: true,
        pinned: false,
    },
    retainerStatus: {
        id: 22,
        name: "retainer_status",
        label: "Retainer Status",
        value: null,
        defaultValue: null,
        placeholder: '',
        options: [
            {value: null, label: "------------"},
            {value: "retainer", label: "Retainer"},
            {value: "no_retainer", label: "No Retainer"}
        ],
        widget: 'list-box',
        multiSelect: false,
        awaitingOptions: false,
        visible: true,
        pinned: false,
    },
    engagementStatus: {
        id: 22,
        name: "engagement_status",
        label: "Engagement Status",
        value: null,
        defaultValue: null,
        placeholder: '',
        options: [
            {value: null, label: "------------"},
            {value: "active", label: "Active"},
            {value: "inactive", label: "Inactive"},
            {value: "paused", label: "Paused"}

        ],
        widget: 'list-box',
        multiSelect: false,
        awaitingOptions: false,
        visible: true,
        pinned: false,
    },
    tailStatus: {
        id: 22,
        name: "tail_status",
        label: "Tail Status",
        value: null,
        defaultValue: null,
        placeholder: '',
        options: [
            {value: null, label: "------------"},
            {value: "before_tail_period", label: "Before Tail Period"},
            {value: "in_tail_period", label: "In Tail Period"},
            {value: "after_tail_period", label: "After Tail Period"},
        ],
        widget: 'list-box',
        multiSelect: false,
        awaitingOptions: false,
        visible: true,
        pinned: false,
    },
    accountStaffMembers: {
        id: 22,
        name: "account_staff_members",
        label: "Account Staff Members",
        value: [],
        defaultValue: [],
        placeholder: '',
        options: [
            {value: "clear", label: "------------"}
        ],
        widget: 'list-box',
        multiSelect: true,
        setOptionsMethod: loadAccountStaffMembersOptions.bind(null),
        visible: true,
        pinned: false,
    },
    accountManager: {
        id: 22,
        name: "account_manager",
        label: "Account Manager",
        value: [],
        defaultValue: [],
        placeholder: '',
        options: [
            {value: "clear", label: "------------"}
        ],
        widget: 'list-box',
        multiSelect: true,
        setOptionsMethod: loadAccountManagerOptions.bind(null),
        visible: true,
        pinned: false,
    },
    generalText: {
        id: 4,
        name: "general_text",
        label: "General Text Search",
        placeholder: 'Name, Address, Note, etc.',
        value: null,
        defaultValue: null,
        type: 'text',
        widget: 'input',
        multiSelect: false,
        visible: true,
        pinned: false,
    },
    
})


async function submitEditForm(id, formData, closeAfterSave) {
  const token = store.state.auth.token;
  const result = await editClient(id, formData, token);
  if (result.status == 200) {
    $toast.open({
      message: `Successfully updated ${displayedClient.value.client_name}.`,
      type: 'success',
      duration: 3000
    })
  } else {
    $toast.open({
      message: `Error, ${displayedClient.value.client_name} not updated.`,
      type: 'error',
      duration: 3000
    })
  }
  reloadClients();
  if(closeAfterSave){
        closeEditForm();
  }
}

const clientBulkOperations = reactive({})

const displayedClient = ref(null)

const clientRowActions = reactive({
    openEditForm: {
        performAction(actionData) {
            displayedClient.value = actionData.client
            visibleEditForm.value = true
        }
    },
    openAllEngagements: {
        performAction(actionData) {
            displayedClient.value = actionData.client
            visibleEngagementsTable.value = true
        }
    },
    openAllInvoices: {
        performAction(actionData) {
            displayedClient.value = actionData.client
            visibleInvoicesTable.value = true
        }
    },
    openAllWorkflowSteps: {
        performAction(actionData) {
            displayedClient.value = actionData.client
            visibleEditForm.value = true
            defaultTab.value = 'Workflow'
        }
    },
    openAllClientPosts: {
        performAction(actionData) {
            displayedClient.value = actionData.client
            visibleEditForm.value = true
            defaultTab.value = 'Posts'
        }
    },
    addContact: {
        performAction(actionData) {
            displayedClient.value = actionData.client
            visibleNewContactForm.value = true
        }
    },
    openAllContacts: {
        performAction(actionData) {
            displayedClient.value = actionData.client
            visibleContactList.value = true
        }
    },
})


const ACCOUNT_MANAGER_USERNAMES = ['scott_shedd', 'brandon_ahearn', 'corgan_barber', 'jed_golman']

async function loadAccountManagerOptions() {
    const token = store.state.auth.token
    const payload = {
        endpoint: "?exclude_dev=True",
        token: token
    }
    const response = await getUsers(payload)
    clientFilters.accountManager.options = [{value: "null", label: "Unassigned"}, ...response.users
        .filter((user) => ACCOUNT_MANAGER_USERNAMES.includes(user.username))
        .map((user) => {
            return {value: user.id, label: `${user.first_name} ${user.last_name}`}
        })]
}


async function loadAccountStaffMembersOptions() {
    // console.log('loadAccountStaffMembersOptions')
    const token = store.state.auth.token
    const payload = {
        endpoint: "?group_id=1&group_id=7&group_id=8&exclude_dev=True",
        token: token
    }
    const response = await getUsers(payload)
    clientFilters.accountStaffMembers.options = [{value: null, label: "------------"}, ...response.users.map((user) => {
        return {value: user.id, label: `${user.first_name} ${user.last_name}`}
    })]
}

const clientSummaryActions = reactive({})

const columnSpan = computed(() => {
    let numHeaders = tableHeaders.value.length
    return menuOptions.selectAllHeader ? numHeaders + 1 : numHeaders
})

function handleActionResponse(response, buttonId, errorMessage) {
    console.log(response)
    if(response.status == 200) {
        $toast.open({
            type: 'success',
            duration: 5000,
            message: response.data.status ? response.data.status : 'Successfully completed action.',
        })
        reloadClients()
    } else {
        $toast.open({
            type: 'error',
            duration: 5000,
            message: errorMessage
        })
    }
    actionButtonId.value = buttonId
}

function reloadClients() {
    shouldReload.value = !shouldReload.value
}

function setFilters(updatedFilters) {
    clientFilters.status = updatedFilters.status;
}

function clearFilter(filterKey) {
    if(filterKey.endsWith('.min')) {
        clientFilters[filterKey.replace('.min', '')].min.value = null
    } else if(filterKey.endsWith('.max')) {
            clientFilters[filterKey.replace('.max', '')].max.value = null
    } else if(Array.isArray(clientFilters[filterKey].value)){
        clientFilters[filterKey].value = []
    } else {
        clientFilters[filterKey].value = null
    }
}


function closeEditForm() {
    visibleEditForm.value = false
    displayedClient.value = null
    defaultTab.value = null
}

function closeEngagementsTable() {
    visibleEngagementsTable.value = false
    displayedClient.value = null
}

function closeInvoicesTable() {
    visibleInvoicesTable.value = false
    displayedClient.value = null
}

function performRowAction(actionData) {
    actionButtonId.value = null
    // console.log('CompanyPage: performRowAction', actionData)
    clientRowActions[actionData.actionKey].performAction(actionData)
}

async function setFilterOptions(filterName, queryParams='') {
    // console.log('CompanyPage: setFilterOptions', filterName)
    if(!clientFilters[filterName].awaitingOptions){
        if(clientFilters[filterName].setOptionsMethod){
            clientFilters[filterName].awaitingOptions = true
            clientFilters[filterName].setOptionsMethod(queryParams)
            setTimeout(() => {
                clientFilters[filterName].awaitingOptions = false;
            }, 30000)
        }
    }
}

function closeAllForms(){
    closeEditForm()
    closeEngagementsTable()
    closeInvoicesTable()
}

async function loadClients(fetchFilter='') {
    isLoading.value = true;
    const token = store.state.auth.token
    let orderByInvoiceDay = null
    if(fetchFilter.includes('-next_invoice_day')){
        fetchFilter = fetchFilter.replace('-next_invoice_day', '')
        orderByInvoiceDay = 'desc'
    }
    if(fetchFilter.includes('next_invoice_day')){
        fetchFilter = fetchFilter.replace('next_invoice_day', '')
        orderByInvoiceDay = 'asc'
    }
    const payload = {
        token: token,
        endpoint: fetchFilter
    }
    response.value = await getClients(payload);
    if(orderByInvoiceDay == 'desc'){
       response.value.data.results.sort((a, b) => {
        if (a.days_until_next_invoice_day === null && b.days_until_next_invoice_day !== null) {
            return 1;
        }
        if (a.days_until_next_invoice_day !== null && b.days_until_next_invoice_day === null) {
            return -1;
        }
        if (a.days_until_next_invoice_day === null && b.days_until_next_invoice_day === null) {
            return 0;
        }
        return a.days_until_next_invoice_day - b.days_until_next_invoice_day
    })
    }
    if(orderByInvoiceDay == 'asc'){
       response.value.data.results.sort((a, b) => {
        if (a.days_until_next_invoice_day === null && b.days_until_next_invoice_day !== null) {
            return 1;
        }
        if (a.days_until_next_invoice_day !== null && b.days_until_next_invoice_day === null) {
            return -1;
        }
        if (a.days_until_next_invoice_day === null && b.days_until_next_invoice_day === null) {
            return 0;
        }
        return b.days_until_next_invoice_day - a.days_until_next_invoice_day
    })
    }
    if(displayedClient.value) {
        displayedClient.value = response.value.data.results.find((client) => client.id == displayedClient.value.id)
        if(!displayedClient.value){
            closeAllForms()
        }
    }
    isLoading.value = false;
}

// loadClients()

const workflowSteps = ref([])

async function loadWorkflowSteps(){
    const token = store.state.auth.token
    const result = await getWorkflowSteps(token, '?workflow_type=client_engagement&status=active')
    if(result.status === 200){
        workflowSteps.value = result.data
        // console.log('workflowSteps', workflowSteps.value)
    }
}

loadWorkflowSteps()



const totalWorkflowStepsRatio = computed(() => {
    if(response.value){
        let clients = response.value.data.results
        let numerator = 0
        let denomenator = 0
        clients.forEach((client) => {
            const clientWorkflowSteps = client.workflow_steps.filter(workflowStep => workflowStep.completed_on).map(workflowStep => workflowStep.workflow_step.id)
            let missingStepCount = workflowSteps.value.filter((step) => {
                return step.required && !clientWorkflowSteps.includes(step.id)
            }).length
            let completedStepCount = client.workflow_steps.filter(workflowStep => workflowStep.completed_on && workflowStep.workflow_step.required && workflowStep.workflow_step.status == 'active').length
            let requiredStepCount = workflowSteps.value.filter((step) => step.required).length + client.workflow_steps.filter(workflowStep => !workflowStep.completed_on).length - missingStepCount
            numerator += completedStepCount
            denomenator += requiredStepCount
        })
        return `${numerator}/${denomenator}`  
    } else {
        return '--'
    }
})

const clientTaskTypes = ref([])

async function loadClientTaskTypes(){
  const token = store.state.auth.token
  const result = await getClientTaskTypes(token)
  if(result.status == 200){
    clientTaskTypes.value = result.data
  }
}

const displayedInvoices = computed(() => {
    if(displayedClient.value){
        let invoices = []
        displayedClient.value.engagements.forEach((engagement) => {
            invoices = invoices.concat(engagement.invoices)
        });
        return invoices
    } else {
        return []
    }
})


loadClientTaskTypes()
loadAccountStaffMembersOptions()

// async function loadPinnedFilters() {
//     // console.log('loadPinnedFilters')
//     const token = store.state.auth.token
//     const userId = store.state.auth.user.id
//     const response = await getPinnedUserCompanyFilterFields(token, userId)
//     // console.log(response)
//     setPinnedFilters(response.data)
// }

async function togglePinnedFilter(filterKey) {
    // console.log('togglePinnedFilter', filterKey)
    let pinnedValue = clientFilters[filterKey].pinned
    clientFilters[filterKey].pinned = !clientFilters[filterKey].pinned
    reorderFilters.value = !reorderFilters.value
    // if(pinnedValue) {
    //     await persistUnpinnedFilter(clientFilters[filterKey])
    // } else {
    //     await persistPinnedFilter(clientFilters[filterKey])
    // }
    // loadPinnedFilters()
}
// function fetchAllOptions() {
// }
  
// fetchAllOptions()

function closeContactList(){
    visibleContactList.value = false
}

function closeNewContactForm(){
    visibleNewContactForm.value = false
}

</script>